import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/features/dashboard/components/DashboardLayout';
import { Section } from '@/ui/layout/Section';
import { SheetBadge } from '@/ui/layout/SheetBadge';
import { Button } from '@/ui/Button';
import { Input } from '@/ui/input';
import { Search, Plus, Users, Star } from 'lucide-react';
import { supabaseService, type CustomRule, type GetRulesRequest } from '@/shared/services/supabaseService';
import { toast } from 'sonner';
import RuleCard from '@/features/rules/components/RuleCard';
import CreateRuleDialog from '@/features/rules/components/CreateRuleDialog';
import { Skeleton } from "@/shared/components";
import { cn } from '@/lib/utils';

const RulesLibraryPage: React.FC = () => {
  const [publicRules, setPublicRules] = useState<CustomRule[]>([]);
  const [myRules, setMyRules] = useState<CustomRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingRule, setEditingRule] = useState<CustomRule | undefined>();
  const [activeTab, setActiveTab] = useState<'public' | 'my-rules'>('public');

  const fetchPublicRules = useCallback(async () => {
    try {
      const filters: GetRulesRequest = {
        search: searchTerm || undefined,
        limit: 100
      };

      const response = await supabaseService.getPublicRules(filters);
      setPublicRules(response.rules);
    } catch (error) {
      console.error('Error fetching public rules:', error);
      toast.error('Failed to load public rules');
    }
  }, [searchTerm]);

  const fetchMyRules = useCallback(async () => {
    try {
      const filters: GetRulesRequest = {
        search: searchTerm || undefined,
        is_public: false,
        limit: 100
      };

      const response = await supabaseService.getCustomRules(filters);
      setMyRules(response.rules);
    } catch (error) {
      console.error('Error fetching my rules:', error);
      toast.error('Failed to load your rules');
    }
  }, [searchTerm]);

  const fetchAllRules = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchPublicRules(), fetchMyRules()]);
    setLoading(false);
  }, [fetchPublicRules, fetchMyRules]);

  useEffect(() => {
    fetchAllRules();
  }, [fetchAllRules]);

  const handleCreateRule = async (rule: CustomRule) => {
    setMyRules(prev => [rule, ...prev]);
    setShowCreateDialog(false);
    toast.success('Rule created successfully');
  };

  const handleUpdateRule = async (updatedRule: CustomRule) => {
    setMyRules(prev => prev.map(rule => rule.id === updatedRule.id ? updatedRule : rule));
    setEditingRule(undefined);
    toast.success('Rule updated successfully');
  };

  const handleDeleteRule = async (ruleId: string) => {
    try {
      await supabaseService.deleteCustomRule(ruleId);
      setMyRules(prev => prev.filter(rule => rule.id !== ruleId));
      toast.success('Rule deleted successfully');
    } catch (error) {
      console.error('Error deleting rule:', error);
      toast.error('Failed to delete rule');
    }
  };

  const handleTogglePublic = async (ruleId: string, isPublic: boolean) => {
    try {
      const updatedRule = await supabaseService.updateCustomRule(ruleId, { is_public: isPublic });
      setMyRules(prev => prev.map(rule => rule.id === ruleId ? updatedRule : rule));

      if (isPublic) {
        setPublicRules(prev => [updatedRule, ...prev]);
      } else {
        setPublicRules(prev => prev.filter(rule => rule.id !== ruleId));
      }

      toast.success(`Rule ${isPublic ? 'made public' : 'made private'}`);
    } catch (error) {
      console.error('Error updating rule visibility:', error);
      toast.error('Failed to update rule visibility');
    }
  };

  const handleCopyRule = async (rule: CustomRule) => {
    try {
      const newRule = await supabaseService.createCustomRule({
        title: `${rule.title} (Copy)`,
        description: rule.description,
        rule_content: rule.rule_content,
        is_public: false
      });

      setMyRules(prev => [newRule, ...prev]);
      toast.success('Rule copied successfully');
    } catch (error) {
      console.error('Error copying rule:', error);
      toast.error('Failed to copy rule');
    }
  };

  const handleUseRule = async (rule: CustomRule, repositoryIds: string[]) => {
    try {
      // Add the rule to each selected repository
      await Promise.all(
        repositoryIds.map(repoId =>
          supabaseService.addRuleToRepository(repoId.toString(), rule.id)
        )
      );

      toast.success(`Rule applied to ${repositoryIds.length} repository(ies)`);
    } catch (error) {
      console.error('Error applying rule:', error);
      toast.error('Failed to apply rule to repositories');
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  // Mock navigation handler for DashboardLayout (since this page is top-level now in the nav structure)
  const handleTabChange = (tab: string) => {
      // If we were inside DashboardPage we would use this, but here we might need to navigate
      // Since DashboardLayout expects an onTabChange, and we are hijacking it for this page:
      if (tab !== 'rules') {
          window.location.href = `/dashboard?tab=${tab}`;
      }
  };

  return (
    <DashboardLayout activeTab="rules" onTabChange={handleTabChange}>
      <Section className="p-8 md:p-12" label="LIBRARY_CONTEXT">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <SheetBadge className="mb-4">RULES_LIBRARY</SheetBadge>
            <h1 className="text-3xl md:text-4xl font-bold font-mono tracking-tight uppercase">
              System Rules
            </h1>
            <p className="mt-2 text-muted-foreground font-mono text-sm border-l-2 border-neutral-950 dark:border-neutral-100 pl-3">
              Standardize your engineering practices with reusable instructions.
            </p>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="rounded-none font-mono uppercase font-bold bg-neutral-950 text-white dark:bg-white dark:text-black hover:bg-neutral-800 dark:hover:bg-neutral-200"
          >
            <Plus className="h-4 w-4 mr-2" />
            [ CREATE_RULE ]
          </Button>
        </div>
      </Section>

      <Section className="px-8 py-8 md:px-12" label="BROWSE_RULES">
        {/* Search & Filter Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="SEARCH_RULES_DB..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10 h-12 rounded-none font-mono text-sm border-neutral-950 dark:border-neutral-100 focus-visible:ring-0"
                />
            </div>

            <div className="flex border border-neutral-950 dark:border-neutral-100 p-1 bg-neutral-100 dark:bg-neutral-900 gap-1">
                <button
                    onClick={() => setActiveTab('public')}
                    className={cn(
                        "px-4 py-2 font-mono text-xs uppercase font-bold flex items-center gap-2 transition-colors",
                        activeTab === 'public'
                            ? "bg-white dark:bg-black text-foreground shadow-sm border border-neutral-200 dark:border-neutral-800"
                            : "text-muted-foreground hover:text-foreground"
                    )}
                >
                    <Users className="h-3 w-3" />
                    PUBLIC ({publicRules.length})
                </button>
                <button
                    onClick={() => setActiveTab('my-rules')}
                    className={cn(
                        "px-4 py-2 font-mono text-xs uppercase font-bold flex items-center gap-2 transition-colors",
                        activeTab === 'my-rules'
                            ? "bg-white dark:bg-black text-foreground shadow-sm border border-neutral-200 dark:border-neutral-800"
                            : "text-muted-foreground hover:text-foreground"
                    )}
                >
                    <Star className="h-3 w-3" />
                    MY_RULES ({myRules.length})
                </button>
            </div>
        </div>

        {/* Content Area */}
        {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
                <div key={i} className="border border-neutral-200 dark:border-neutral-800 p-4 h-48 space-y-4">
                    <Skeleton className="h-6 w-3/4 rounded-none" />
                    <Skeleton className="h-4 w-full rounded-none" />
                    <Skeleton className="h-4 w-5/6 rounded-none" />
                </div>
            ))}
            </div>
        ) : (
            <>
                {activeTab === 'public' && (
                    <>
                        <div className="mb-6 flex items-center gap-2">
                            <span className="w-2 h-2 bg-blue-500 rounded-none inline-block"></span>
                            <h3 className="font-mono text-sm font-bold uppercase text-muted-foreground">Community Standards</h3>
                        </div>

                        {publicRules.length === 0 ? (
                            <div className="border-2 border-dashed border-neutral-300 dark:border-neutral-700 p-12 text-center">
                                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                <h3 className="font-mono font-bold text-lg uppercase mb-2">NO_RESULTS</h3>
                                <p className="font-mono text-xs text-muted-foreground">
                                    No public rules match your search criteria.
                                </p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
                                {publicRules.map(rule => (
                                    <RuleCard
                                        key={rule.id}
                                        rule={rule}
                                        onCopy={handleCopyRule}
                                        onUseRule={handleUseRule}
                                        showActions={false}
                                        showUsage={true}
                                        showUseButton={true}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}

                {activeTab === 'my-rules' && (
                    <>
                        <div className="mb-6 flex items-center gap-2">
                            <span className="w-2 h-2 bg-yellow-500 rounded-none inline-block"></span>
                            <h3 className="font-mono text-sm font-bold uppercase text-muted-foreground">My Collection</h3>
                        </div>

                        {myRules.length === 0 ? (
                            <div className="border-2 border-dashed border-neutral-300 dark:border-neutral-700 p-12 text-center">
                                <Star className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                <h3 className="font-mono font-bold text-lg uppercase mb-2">EMPTY_COLLECTION</h3>
                                <p className="font-mono text-xs text-muted-foreground mb-6">
                                    You haven't created any rules yet.
                                </p>
                                <Button
                                    onClick={() => setShowCreateDialog(true)}
                                    variant="outline"
                                    className="rounded-none font-mono uppercase text-xs"
                                >
                                    <Plus className="h-3 w-3 mr-2" />
                                    Initialize First Rule
                                </Button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
                                {myRules.map(rule => (
                                    <RuleCard
                                        key={rule.id}
                                        rule={rule}
                                        onEdit={setEditingRule}
                                        onDelete={handleDeleteRule}
                                        onTogglePublic={handleTogglePublic}
                                        onCopy={handleCopyRule}
                                        showActions={true}
                                        showUsage={true}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </>
        )}
      </Section>

      {/* Dialogs */}
      <CreateRuleDialog
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSuccess={handleCreateRule}
      />

      <CreateRuleDialog
        isOpen={!!editingRule}
        onClose={() => setEditingRule(undefined)}
        onSuccess={handleUpdateRule}
        editingRule={editingRule}
      />
    </DashboardLayout>
  );
};

export default RulesLibraryPage;
